import { LercTileLayer } from "@gago-react-gl/core";
import Axios from "axios";
import {
  LercTilePaint,
  RasterColorscalePaintPropertyObject
} from "gago-mapbox-gl";
import * as React from "react";
import throttle from "../../util/throttle";
import {
  BeforeAble,
  GradientLegendProps,
  Legendble,
  LercMaskable,
  Maskable,
  OnlinePaint,
  Opacityable,
  Showable
} from "../interfaces";
import { SmartLercLayer } from "../smart-layer";
import { SmartLercSource, SmartLercSourceProps } from "../smart-lerc-source";
// import "./gradient-layer.css";

interface GradientLayerPaintProps {
  /**
   * 渐变值的上限
   */
  valueMax: number;
  /**
   * 渐变值的下限
   */
  valueMin: number;
  /**
   * 渐变的颜色
   */
  colors: string[];
  /**
   * 渐变节点控制，如果不传，会根据colors生成一个均匀的色带
   */
  positions?: number[];
  /**
   * 渐变类型
   * @default "exponential"
   * @var exponential 过渡
   * @var interval 阶梯
   * @demo [online](http://gallery.echartsjs.com/editor.html?c=xr1nH2_5km)
   */
  colorscaleType?: "exponential" | "interval";
}

export interface SourceProps {
  /**
   * 唯一标示
   */
  id: SmartLercSourceProps["id"];
  /**
   * 数据源
   */
  url: SmartLercSourceProps["url"];
  /**
   * 经纬度范围
   * !!只有 image 数据源才需要配置
   */
  coordinates?: SmartLercSourceProps["coordinates"];
}

export type GradientLayerProps = (
  | (GradientLayerPaintProps & SmartLercSourceProps)
  | ({
      /**
       * 配色配置的地址
       *
       * @type {string}
       */
      paintUrl: string;
    } & SourceProps)) & {
  /**
   * 只渲染的值域
   *
   * @type {Array<[number,number]>}
   */
  include?: Array<[number, number]>;
} & Maskable &
  LercMaskable &
  Opacityable &
  Showable &
  BeforeAble &
  Legendble<GradientLegendProps>;
export interface GradientLayerState {
  /**
   * 渲染范围
   *
   * @type {Array<[number,number]>}
   * @memberof GradientLayerState
   */
  include?: Array<[number, number]>;
  paint?: OnlinePaint;
}
/**
 * 渐变图层
 *
 * @export
 * @class GradientLayer
 * @extends {React.Component<GradientLayerProps, GradientLayerState>}
 */
export class GradientLayer extends React.PureComponent<
  GradientLayerProps,
  GradientLayerState
> {
  static defaultProps = {
    show: true,
    colorscaleType: "exponential" as "exponential"
  };

  state: GradientLayerState = {
    include: undefined,
    paint: undefined
  };

  counter = 0;

  private includeBuff: Array<[number, number]>;

  /**
   * 节流器，防止卡顿
   *
   * @private
   * @memberof GradientLayer
   */
  private changeIncludeWithThrottle = throttle(
    () => this.changeInclude(this.includeBuff),
    0
  );

  componentDidMount() {
    if (this.hasOnlinePaint()) {
      Axios.get<{ data: { colour: OnlinePaint } }>(
        (this.props as { paintUrl: string }).paintUrl
      ).then(res => {
        this.setState({ paint: res.data.data.colour });
      });
    }
  }

  render() {
    let source: JSX.Element;
    let layer: JSX.Element;
    let colors: RasterColorscalePaintPropertyObject["stops"];

    if (this.hasOnlinePaint()) {
      if (this.onlinePaintLoaded()) {
        source = this.generateSource_();
        layer = this.generateLayer_();
        const colorscale = this.getOnlinePaint().paint["raster-colorscale"];
        colors = colorscale.stops.concat();
        try {
          colors.unshift([
            this.getOnlinePaint().paint["raster-domain-min"],
            colors[0][1]
          ]);
          colors.push([
            this.getOnlinePaint().paint["raster-domain-max"],
            colors[colors.length - 1][1]
          ]);
        } catch (e) {
          throw new Error(`layer ${this.props.id} 缺少渐变色配置`);
        }
      } else {
        return null;
      }
    } else {
      source = this.generateSource_();
      layer = this.generateLayer_();
      const colorscale = this.generateColorscale_();

      console.log(colorscale);
      colors = colorscale.stops.concat();
      try {
        colors.unshift([this.getPropsPaint().valueMin, colors[0][1]]);
        colors.push([
          this.getPropsPaint().valueMax,
          colors[colors.length - 1][1]
        ]);
      } catch (e) {
        throw new Error(`layer ${this.props.id} 缺少渐变色配置`);
      }
    }

    const gradientLegendProps: GradientLegendProps = {
      colors,
      include: this.getInclude(),
      changeInclude: this.changeIncludeThroughtThrottle
    };
    let a: React.ReactNode;
    if (this.props.children) {
      a = this.props.children(gradientLegendProps);
    }

    return (
      <>
        {source}
        {layer}
        {a}
      </>
    );
  }

  private changeInclude = (include: Array<[number, number]>) => {
    console.log(this.counter++);
    this.setState({
      include
    });
  };

  private changeIncludeThroughtThrottle = (
    include: Array<[number, number]>
  ) => {
    this.includeBuff = include;
    this.changeIncludeWithThrottle();
  };

  private generateSource_() {
    if (this.hasOnlinePaint()) {
      if (this.onlinePaintLoaded()) {
        const { id, url, coordinates } = this.props;
        return (
          <SmartLercSource
            key="source"
            id={id}
            url={url}
            sourceMaxzoom={this.getOnlinePaint().maxzoom}
            sourceMinzoom={this.getOnlinePaint().minzoom}
            coordinates={coordinates}
          />
        );
      } else {
        throw new Error(`${this.props.id}缺少在线配色`);
      }
    } else {
      const { id, url, sourceMaxzoom, sourceMinzoom, coordinates } = this
        .props as SmartLercSourceProps;
      return (
        <SmartLercSource
          key="source"
          id={id}
          url={url}
          sourceMaxzoom={sourceMaxzoom}
          sourceMinzoom={sourceMinzoom}
          coordinates={coordinates}
        />
      );
    }
  }

  private getInclude() {
    let valueMax: number;
    let valueMin: number;
    if (this.hasOnlinePaint()) {
      if (this.onlinePaintLoaded()) {
        valueMax = this.getOnlinePaint().paint["raster-domain-max"] as number;
        valueMin = this.getOnlinePaint().paint["raster-domain-min"] as number;
      } else {
        throw new Error(`${this.props.id} 配色未加载完成`);
      }
    } else {
      valueMax = this.getPropsPaint().valueMax;
      valueMin = this.getPropsPaint().valueMin;
    }

    let include: Array<[number, number]> = this.state.include
      ? this.state.include
      : [[valueMin, valueMax]];
    include = this.props.include ? this.props.include : include;
    return include;
  }

  private generateLayer_() {
    const {
      id,
      show,
      opacity,
      maskSourceId,
      value,
      before,
      lercMaskSourceId,
      lercMaskInclude
    } = this.props;
    const { valueMin, valueMax } = this.getPropsPaint();

    let paint: LercTilePaint;
    let colorscale;
    const include = this.getInclude();

    if (this.hasOnlinePaint()) {
      if (this.onlinePaintLoaded()) {
        paint = {
          "raster-domain-max": this.getOnlinePaint().paint["raster-domain-max"],
          "raster-domain-min": this.getOnlinePaint().paint["raster-domain-min"],
          "lerc-include": include,
          "raster-colorscale": this.getOnlinePaint().paint["raster-colorscale"]
        };
      } else {
        throw new Error(`${this.props.id} 配色未加载完成`);
      }
    } else {
      colorscale = this.generateColorscale_();
      paint = {
        "raster-domain-max": valueMax,
        "raster-domain-min": valueMin,
        "lerc-include": include,
        "raster-colorscale": colorscale
      };
    }

    return (
      <SmartLercLayer<LercTileLayer>
        value={value}
        key="layer"
        show={show}
        id={id}
        type="lercv2"
        source={id}
        paint={paint}
        opacity={opacity}
        maskSourceId={maskSourceId}
        lercMaskSourceId={lercMaskSourceId}
        lercMaskInclude={lercMaskInclude}
        before={before}
      />
    );
  }

  private generateColorscale_(): RasterColorscalePaintPropertyObject {
    const {
      valueMax,
      valueMin,
      colors,
      positions,
      colorscaleType
    } = this.getPropsPaint();
    if (positions) {
      const stops: Array<[number, string]> = positions.map(
        (pos: number, index): [number, string] => [pos, colors[index]]
      );
      return {
        stops,
        type: colorscaleType,
        max: valueMax,
        min: valueMin
      };
    } else {
      const stops: Array<[number, string]> = caculateColorScale(
        colors,
        valueMax,
        valueMin
      );
      return {
        stops,
        type: colorscaleType,
        max: valueMax,
        min: valueMin
      };
    }
  }

  /**
   * 从props中获取paint
   *
   * @author 张卓诚
   * @date 2019-02-18
   * @private
   * @returns
   * @memberof GradientLayer
   */
  private getPropsPaint() {
    return this.props as GradientLayerPaintProps;
  }

  /**
   * 获取线上配色
   *
   * @author 张卓诚
   * @date 2019-02-18
   * @private
   * @memberof GradientLayer
   */
  private getOnlinePaint() {
    return this.state.paint as OnlinePaint;
  }

  /**
   * 在线配色加载完成
   *
   * @author 张卓诚
   * @date 2019-02-18
   * @private
   * @memberof GradientLayer
   */
  private onlinePaintLoaded() {
    return this.state.paint !== undefined;
  }

  /**
   * 有线上配色
   *
   * @author 张卓诚
   * @date 2019-02-18
   * @private
   * @memberof GradientLayer
   */
  private hasOnlinePaint() {
    const paintUrl = (this.props as { paintUrl: string }).paintUrl;
    return paintUrl !== undefined;
  }
}

function caculateColorScale(
  array: string[],
  max: number,
  min: number = 0
): Array<[number, string]> {
  const length = array.length;
  const step = (max - min) / (length - 1);
  const results: Array<[number, string]> = [];
  for (let i = 0; i < length; i++) {
    const pos = min + step * i;
    results.push([pos, array[i]]);
  }
  return results;
}
